import React, { useState, useEffect } from 'react';
import { useClerk, UserProfile, useUser } from '@clerk/clerk-react';
import { LogOut, Globe, User, Settings, ChevronRight, Mail, Calendar, Shield } from 'lucide-react';
import { useAppStore } from '../store';
import { useTranslation } from '../utils/translations';
import PushNotificationSetup from './PushNotificationSetup';

export default function ProfilePage() {
    const { signOut } = useClerk();
    const { user } = useUser();
    const { t } = useTranslation();
    const [language, setLanguage] = useState(localStorage.getItem('app_language') || 'it');
    const [activeSection, setActiveSection] = useState('main'); // 'main', 'clerk_profile'

    const handleLanguageChange = (lang) => {
        setLanguage(lang);
        localStorage.setItem('app_language', lang);
        window.location.reload();
    };

    const languages = [
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'es', name: 'Español', flag: '🇪🇸' }
    ];

    if (activeSection === 'clerk_profile') {
        return (
            <div className="max-w-4xl mx-auto h-[600px]">
                <button
                    onClick={() => setActiveSection('main')}
                    className="mb-4 flex items-center text-slate-400 hover:text-white transition-colors"
                >
                    <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
                    {t('back_to_settings')}
                </button>

                <div className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 h-full">
                    <UserProfile
                        path="/profile"
                        routing="virtual"
                        appearance={{
                            elements: {
                                rootBox: "w-full h-full",
                                card: "bg-slate-900 shadow-none w-full h-full border-none",
                                scrollBox: "bg-slate-900",
                                navbar: "bg-slate-900 border-r border-slate-800",
                                navbarButton: "text-slate-400 hover:text-white hover:bg-slate-800",
                                headerTitle: "text-white",
                                headerSubtitle: "text-slate-400",
                                profileSectionTitleText: "text-white",
                                profileSectionPrimaryButton: "text-violet-400 hover:text-violet-300 hover:bg-slate-800",
                                accordionTriggerButton: "text-slate-200",
                                formButtonPrimary: "bg-violet-600 hover:bg-violet-700",
                                formFieldLabel: "text-slate-300",
                                formFieldInput: "bg-slate-800 border-slate-700 text-white",
                                footer: "hidden"
                            },
                            variables: {
                                colorBackground: "#0f172a",
                                colorText: "white",
                                colorInputBackground: "#1e293b",
                                colorInputText: "white"
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">{t('your_profile')}</h2>
                <p className="text-slate-400">{t('manage_preferences')}</p>
            </div>

            {/* User Info Card */}
            <div className="glass-card p-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                        <User className="w-5 h-5 text-violet-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white">{t('account_info')}</h3>
                </div>

                <div className="space-y-3">
                    {/* Email */}
                    <div className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                        <Mail className="w-5 h-5 text-blue-400 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-xs text-slate-500 mb-1">{t('email')}</div>
                            <div className="text-sm font-medium text-white">
                                {user?.primaryEmailAddress?.emailAddress || t('not_set')}
                            </div>
                        </div>
                    </div>

                    {/* Join Date */}
                    <div className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                        <Calendar className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-xs text-slate-500 mb-1">{t('member_since')}</div>
                            <div className="text-sm font-medium text-white">
                                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : 'en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                }) : t('not_available')}
                            </div>
                        </div>
                    </div>

                    {/* Account Status */}
                    <div className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                        <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-xs text-slate-500 mb-1">{t('account_status')}</div>
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${user?.emailAddresses?.[0]?.verification?.status === 'verified' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                                <span className="text-sm font-medium text-white">
                                    {user?.emailAddresses?.[0]?.verification?.status === 'verified' ? t('verified') : t('unverified')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Language Selector */}
            <div className="glass-card p-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Globe className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white">{t('language')}</h3>
                </div>

                <div className="space-y-2">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${language === lang.code
                                ? 'bg-blue-500/20 border-blue-500/50 text-white'
                                : 'bg-slate-800/30 border-slate-700/30 text-slate-400 hover:bg-slate-800/50'
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-xl">{lang.flag}</span>
                                <span className="font-medium">{lang.name}</span>
                            </div>
                            {language === lang.code && (
                                <div className="w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.5)]" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Push Notifications Setup */}
            <div className="glass-card p-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                        <Settings className="w-5 h-5 text-violet-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Notifiche Push</h3>
                </div>
                <PushNotificationSetup />
            </div>

            {/* Logout Button */}
            <button
                onClick={() => signOut()}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 font-bold hover:bg-red-500/20 transition-all mt-8"
            >
                <LogOut className="w-5 h-5" />
                {t('logout')}
            </button>

            <div className="text-center text-xs text-slate-600 mt-8">
                TradeAnalyzer Pro v1.0.3
                <br />
                ID: {useAppStore.getState().accountInfo?.login || 'Guest'}
            </div>
        </div>
    );
}
